import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../core/services/api';
import { NotificationService } from '../../core/services/notification';
import { CustomValidators } from '../../shared/validators/custom-validators';

@Component({
    selector: 'app-membership',
    standalone: true,
    imports: [CommonModule, TranslateModule, ReactiveFormsModule],
    template: `
    <div class="bg-gray-50 min-h-screen py-12">
      <div class="container mx-auto px-4 max-w-4xl">
        <!-- Header -->
        <div class="text-center mb-12">
          <h1 class="text-4xl font-bold mb-4 uppercase text-enduro-black">{{ 'NAV.MEMBERSHIP' | translate }}</h1>
          <div class="w-24 h-1 bg-enduro-orange mx-auto mb-8"></div>
          <p class="text-gray-600 text-lg">
            {{ 'MEMBERSHIP.DESCRIPTION' | translate }}
          </p>
        </div>

        <div class="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col md:flex-row">
            <!-- Info Side -->
            <div class="bg-enduro-black text-white p-8 md:w-1/3 flex flex-col justify-center relative overflow-hidden">
                <div class="absolute inset-0 bg-enduro-rust opacity-20 transform -skew-y-12 scale-150"></div>
                <div class="relative z-10">
                    <h3 class="text-2xl font-bold mb-6 text-enduro-orange">{{ 'MEMBERSHIP.WHY_JOIN_TITLE' | translate }}</h3>
                    <ul class="space-y-4">
                        <li class="flex items-start">
                            <span class="text-enduro-orange mr-2">✓</span>
                            <span>{{ 'MEMBERSHIP.BENEFITS.DISCOUNTS' | translate }}</span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-enduro-orange mr-2">✓</span>
                            <span>{{ 'MEMBERSHIP.BENEFITS.KIT' | translate }}</span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-enduro-orange mr-2">✓</span>
                            <span>{{ 'MEMBERSHIP.BENEFITS.COMMUNITY' | translate }}</span>
                        </li>
                    </ul>
                    <div class="mt-12">
                        <p class="text-sm text-gray-400">{{ 'MEMBERSHIP.PRICE_LABEL' | translate }}</p>
                        <p class="text-4xl font-bold text-white">€ 15,00</p>
                    </div>
                </div>
            </div>

            <!-- Form Side -->
            <div class="p-8 md:w-2/3">
                <h3 class="text-2xl font-bold mb-6 text-gray-800">{{ 'MEMBERSHIP.FORM.TITLE' | translate }}</h3>
                <form [formGroup]="membershipForm" (ngSubmit)="onSubmit()" class="grid grid-cols-1 gap-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-bold text-gray-700 mb-1">
                                {{ 'MEMBERSHIP.FORM.NAME' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="text" 
                                formControlName="name"
                                [placeholder]="'MEMBERSHIP.FORM.PLACEHOLDERS.NAME' | translate"
                                [class.border-red-500]="membershipForm.get('name')?.invalid && membershipForm.get('name')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (membershipForm.get('name')?.invalid && membershipForm.get('name')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (membershipForm.get('name')?.errors?.['required']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.NAME_REQUIRED' | translate }}
                                    }
                                    @if (membershipForm.get('name')?.errors?.['minlength']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.NAME_LENGTH' | translate }}
                                    }
                                    @if (membershipForm.get('name')?.errors?.['hasNumbers']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.NAME_NO_NUMBERS' | translate }}
                                    }
                                </p>
                            }
                        </div>

                        <!-- Surname -->
                        <div>
                            <label class="block text-sm font-bold text-gray-700 mb-1">
                                {{ 'MEMBERSHIP.FORM.SURNAME' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="text" 
                                formControlName="surname"
                                [placeholder]="'MEMBERSHIP.FORM.PLACEHOLDERS.SURNAME' | translate"
                                [class.border-red-500]="membershipForm.get('surname')?.invalid && membershipForm.get('surname')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (membershipForm.get('surname')?.invalid && membershipForm.get('surname')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (membershipForm.get('surname')?.errors?.['required']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.SURNAME_REQUIRED' | translate }}
                                    }
                                    @if (membershipForm.get('surname')?.errors?.['minlength']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.SURNAME_LENGTH' | translate }}
                                    }
                                    @if (membershipForm.get('surname')?.errors?.['hasNumbers']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.SURNAME_NO_NUMBERS' | translate }}
                                    }
                                </p>
                            }
                        </div>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Email -->
                        <div>
                            <label class="block text-sm font-bold text-gray-700 mb-1">
                                {{ 'MEMBERSHIP.FORM.EMAIL' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="email" 
                                formControlName="email"
                                [placeholder]="'MEMBERSHIP.FORM.PLACEHOLDERS.EMAIL' | translate"
                                [class.border-red-500]="membershipForm.get('email')?.invalid && membershipForm.get('email')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (membershipForm.get('email')?.invalid && membershipForm.get('email')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (membershipForm.get('email')?.errors?.['required']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.EMAIL_REQUIRED' | translate }}
                                    }
                                    @if (membershipForm.get('email')?.errors?.['email']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.EMAIL_INVALID' | translate }}
                                    }
                                </p>
                            }
                        </div>

                        <!-- Phone -->
                        <div>
                            <label class="block text-sm font-bold text-gray-700 mb-1">
                                {{ 'MEMBERSHIP.FORM.PHONE' | translate }} <span class="text-red-600">*</span>
                            </label>
                            <input 
                                type="tel" 
                                formControlName="phone"
                                [placeholder]="'MEMBERSHIP.FORM.PLACEHOLDERS.PHONE' | translate"
                                [class.border-red-500]="membershipForm.get('phone')?.invalid && membershipForm.get('phone')?.touched"
                                class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                            >
                            @if (membershipForm.get('phone')?.invalid && membershipForm.get('phone')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    @if (membershipForm.get('phone')?.errors?.['required']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.PHONE_REQUIRED' | translate }}
                                    }
                                    @if (membershipForm.get('phone')?.errors?.['invalidPhone']) {
                                        {{ 'MEMBERSHIP.FORM.ERRORS.PHONE_INVALID' | translate }}
                                    }
                                </p>
                            }
                        </div>
                    </div>

                    <!-- Fiscal Code -->
                     <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">
                            {{ 'MEMBERSHIP.FORM.FISCAL_CODE' | translate }} <span class="text-red-600">*</span>
                        </label>
                        <input 
                            type="text" 
                            formControlName="fiscal_code"
                            [placeholder]="'MEMBERSHIP.FORM.PLACEHOLDERS.FISCAL_CODE' | translate"
                            [class.border-red-500]="membershipForm.get('fiscal_code')?.invalid && membershipForm.get('fiscal_code')?.touched"
                            class="w-full h-11 rounded-lg border-2 border-gray-300 shadow-sm sm:text-sm px-3 uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-enduro-orange"
                        >
                        @if (membershipForm.get('fiscal_code')?.invalid && membershipForm.get('fiscal_code')?.touched) {
                            <p class="text-xs text-red-600 mt-1">
                                @if (membershipForm.get('fiscal_code')?.errors?.['required']) {
                                    {{ 'MEMBERSHIP.FORM.ERRORS.FISCAL_CODE_REQUIRED' | translate }}
                                }
                                @if (membershipForm.get('fiscal_code')?.errors?.['minlength'] || membershipForm.get('fiscal_code')?.errors?.['maxlength']) {
                                    {{ 'MEMBERSHIP.FORM.ERRORS.FISCAL_CODE_LENGTH' | translate }}
                                }
                            </p>
                        }
                    </div>

                    <!-- Privacy -->
                    <div class="flex items-start">
                        <div class="flex h-5 items-center">
                            <input 
                                id="privacy" 
                                type="checkbox" 
                                formControlName="privacy"
                                class="h-4 w-4 rounded border-gray-300 text-enduro-orange focus:ring-enduro-orange transition-all cursor-pointer"
                            >
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="privacy" class="font-medium text-gray-700 cursor-pointer">
                                {{ 'MEMBERSHIP.FORM.PRIVACY' | translate }} <span class="text-red-600">*</span>
                            </label>
                            @if (membershipForm.get('privacy')?.invalid && membershipForm.get('privacy')?.touched) {
                                <p class="text-xs text-red-600 mt-1">
                                    {{ 'MEMBERSHIP.FORM.ERRORS.PRIVACY_REQUIRED' | translate }}
                                </p>
                            }
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        [disabled]="submitting() || membershipForm.invalid"
                        [class.opacity-50]="submitting() || membershipForm.invalid"
                        [class.cursor-not-allowed]="submitting() || membershipForm.invalid"
                        class="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-enduro-orange hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-enduro-orange uppercase tracking-wider transition-all"
                    >
                        {{ submitting() ? ('MEMBERSHIP.FORM.SUBMITTING' | translate) : ('MEMBERSHIP.FORM.SUBMIT' | translate) }}
                    </button>
                </form>
                
            </div>
        </div>
      </div>
    </div>
  `
})
export class MembershipComponent {
    private fb = inject(FormBuilder);
    private api = inject(ApiService);
    private notification = inject(NotificationService);
    private translate = inject(TranslateService);

    submitting = signal(false);

    membershipForm = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2), CustomValidators.noNumbers]],
        surname: ['', [Validators.required, Validators.minLength(2), CustomValidators.noNumbers]],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, CustomValidators.phone]],
        fiscal_code: ['', [Validators.required, CustomValidators.fiscalCode]],
        privacy: [false, Validators.requiredTrue]
    });

    onSubmit() {
        Object.keys(this.membershipForm.controls).forEach(key => {
            this.membershipForm.get(key)?.markAsTouched();
        });

        if (this.membershipForm.invalid) {
            this.translate.get('MEMBERSHIP.FORM.ERRORS.MISSING_FIELDS').subscribe(res => {
                this.notification.showError(res || 'Compila correttamente tutti i campi obbligatori');
            });
            return;
        }

        this.submitting.set(true);

        const payload = {
            first_name: this.membershipForm.value.name,
            last_name: this.membershipForm.value.surname,
            email: this.membershipForm.value.email,
            phone: this.membershipForm.value.phone,
            fiscal_code: this.membershipForm.value.fiscal_code
        };

        this.api.post('membership-requests', payload).subscribe({
            next: () => {
                this.translate.get('MEMBERSHIP.FORM.SUCCESS').subscribe(msg => {
                    this.notification.showSuccess(msg || 'Richiesta inviata con successo!');
                });
                this.membershipForm.reset();
                this.submitting.set(false);
            },
            error: (err) => {
                console.error(err);
                // Extract backend error message
                const backendError = err.error?.error || '';

                if (backendError.includes('Invalid email domain')) {
                    this.translate.get('MEMBERSHIP.FORM.ERRORS.INVALID_EMAIL_DOMAIN').subscribe(res => {
                        this.notification.showError(res);
                    });
                } else if (backendError.includes('Missing required fields')) {
                    this.translate.get('MEMBERSHIP.FORM.ERRORS.MISSING_FIELDS').subscribe(res => {
                        this.notification.showError(res);
                    });
                } else if (backendError) {
                    this.notification.showError(backendError);
                } else {
                    this.translate.get('MEMBERSHIP.FORM.ERROR').subscribe(msg => {
                        this.notification.showError(msg || 'Errore durante l\'invio. Riprova.');
                    });
                }

                this.submitting.set(false);
            }
        });
    }
}
